/*
 * 行为日志（rrweb）接口定义
 *
 * 说明：行为日志上报使用原生 fetch（而非 API/index.js 中的 axios 实例），
 * 避免上报请求经过业务拦截器（token 刷新/加签/错误提示等）后再次触发页面行为，
 * 形成「录制 -> 上报 -> 拦截器 -> 录制」的循环，同时保证页面卸载时可用 keepalive 发送。
 */
import { GetAuthToken } from 'rj-server-auth-token';

const SYS_ID = 'c0c73417cf58cb87e146a78f6e44ef44';
const ACCESS_KEY_SECRET = '7B3A866D-7343-540C-30FD-0E71BE1B667B';
const SERVICE_CODE = 'order-lifecycle';
const BEHAVIOR_PATH = '/behaviorLog';
const DEFAULT_TIMEOUT = 15000;

/**
 * 拼接行为日志接口地址
 */
export function getBehaviorBaseUrl () {
  const env = window._env_ || {};
  const address = env.VUE_APP_REQUEST_ADDRESS || '';
  const service = env.VUE_ENVIRONMENT_ALL || SERVICE_CODE;
  return `${address}/${service}${BEHAVIOR_PATH}`;
}

/**
 * 构造与服务间调用一致的鉴权头
 */
function buildHeaders () {
  const headers = {
    'Content-Type': 'application/json;charset=UTF-8',
    sysId: SYS_ID
  };
  try {
    headers['web-sign-server-auth'] = GetAuthToken({
      sysId: SYS_ID,
      accessKeySecret: ACCESS_KEY_SECRET
    });
  } catch (e) {
    // 加签失败不阻断上报，服务端 SSO 白名单可放行
  }
  try {
    const unionSessionTicket = localStorage.getItem('union_session_ticket');
    if (unionSessionTicket) {
      headers['union_session_ticket'] = unionSessionTicket;
    }
    const sidLogin = localStorage.getItem('sidLogin');
    if (sidLogin) {
      headers['sid_login'] = JSON.parse(sidLogin);
    }
  } catch (e) {
    // 忽略本地存储异常
  }
  return headers;
}

function buildQuery (params) {
  const query = new URLSearchParams();
  Object.keys(params || {}).forEach((key) => {
    const value = params[key];
    if (value !== undefined && value !== null && value !== '') {
      query.append(key, value);
    }
  });
  const text = query.toString();
  return text ? `?${text}` : '';
}

/**
 * 统一请求封装
 *
 * @param {String} path 接口路径，例如 /config
 * @param {Object} options method/params/body/timeout/keepalive
 */
async function request (path, options = {}) {
  const {
    method = 'GET',
    params,
    body,
    timeout = DEFAULT_TIMEOUT,
    keepalive = false
  } = options;
  const url = `${getBehaviorBaseUrl()}${path}${buildQuery(params)}`;

  let timer = null;
  let controller = null;
  if (typeof AbortController !== 'undefined' && !keepalive) {
    controller = new AbortController();
    timer = setTimeout(() => controller.abort(), timeout);
  }

  const fetchOptions = {
    method,
    headers: buildHeaders(),
    credentials: 'include',
    keepalive
  };
  if (controller) {
    fetchOptions.signal = controller.signal;
  }
  if (body !== undefined && body !== null) {
    fetchOptions.body = JSON.stringify(body);
  }

  try {
    const response = await fetch(url, fetchOptions);
    const text = await response.text();
    let result = null;
    try {
      result = text ? JSON.parse(text) : null;
    } catch (e) {
      result = null;
    }
    if (!result) {
      throw new Error(`行为日志接口返回异常(${response.status})`);
    }
    if (String(result.status) !== '200') {
      throw new Error(result.err || `行为日志接口返回异常(${result.status})`);
    }
    return result.data;
  } finally {
    if (timer) {
      clearTimeout(timer);
    }
  }
}

/**
 * 获取录制配置
 */
export function getBehaviorConfig () {
  return request('/config');
}

/**
 * 上报一批 rrweb 事件
 *
 * @param {Object} payload 上报体
 * @param {Object} options keepalive 为 true 时用于页面卸载场景
 */
export function reportBehaviorLog (payload, options = {}) {
  return request('/report', {
    method: 'POST',
    body: payload,
    keepalive: !!options.keepalive,
    timeout: options.timeout || DEFAULT_TIMEOUT
  });
}

/**
 * 分页查询会话列表
 */
export function getBehaviorSessions (params) {
  return request('/sessions', { method: 'GET', params });
}

/**
 * 查询某个会话的完整回放事件
 */
export function getReplayEvents (sessionId) {
  return request(`/replay/${encodeURIComponent(sessionId)}`, { method: 'GET', timeout: 30000 });
}

/**
 * 清理过期行为日志
 */
export function cleanExpiredBehaviorLogs (days) {
  return request('/expired', {
    method: 'DELETE',
    params: { days },
    timeout: 60000
  });
}
